import { Checkbox } from "@hoylu/client-common";
import React, {
  type Dispatch,
  type FC,
  type SetStateAction,
  useState,
} from "react";
import { connect } from "react-redux";
import type { RootState } from "typesafe-actions";
import { login, registerEmail } from "../../state/user/user.actions";
import { Localized } from "../../strings";
import { CenteredContentWithLogo } from "../layouts/CenteredContentWithLogo";
import Styles from "./LoginPrompt.module.css";

const ArrowRight: FC = () => (
  <svg
    aria-hidden={true}
    className={Styles.arrow}
    focusable={false}
    height="12"
    viewBox="0 0 14 12"
    width="14"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M303,21 L308.66,26.836592 M303,32.836593 L308.66,27 M296,26.92 L308.99,26.92"
      fill="none"
      transform="translate(-296 -21)"
    />
  </svg>
);

const termsOfUseUrl = "https://www.hoylu.com/terms-of-use/"; // TODO: Do we have a spot where we store external links?

type RegisterFunction = (email: string) => void;

export const LoginPrompt: React.FC<PromptProps> = ({
  login,
  registerEmail,
  waitingToEditID,
}) => {
  const strings = Localized.object("LOGIN_PROMPT");
  const [enterGuest, setEnterGuest] = useState(false);

  return (
    <CenteredContentWithLogo>
      <p className={Styles.welcome}>{strings.WELCOME_TO_HOYLU}</p>
      {!enterGuest && (
        <React.Fragment>
          <div className={Styles.buttonContainer}>
            <button
              className={Styles.button}
              onClick={login}
              title={strings.LOGIN}
              type="submit"
            >
              <p className={Styles.buttonText}>{strings.LOGIN}</p>
            </button>
          </div>
          <p className={Styles.or}>{strings.OR}</p>
          <div className={Styles.buttonContainer}>
            <button
              className={Styles.button}
              onClick={() => setEnterGuest(true)}
              title={waitingToEditID ? strings.ENTER_AS_GUEST : strings.SIGN_UP}
              type="submit"
            >
              <p className={Styles.buttonText}>
                {waitingToEditID ? strings.ENTER_AS_GUEST : strings.SIGN_UP}
              </p>
            </button>
          </div>
        </React.Fragment>
      )}
      {enterGuest && (
        <RegisterEmailContent
          register={registerEmail}
          setEnterGuest={setEnterGuest}
        />
      )}
    </CenteredContentWithLogo>
  );
};

type RegisterEmailProps = {
  register: RegisterFunction;
  setEnterGuest: Dispatch<SetStateAction<boolean>>;
};

const RegisterEmailContent: React.FunctionComponent<RegisterEmailProps> = ({
  register,
  setEnterGuest,
}) => {
  const strings = Localized.object("LOGIN_PROMPT");
  const [hasAgreed, setHasAgreed] = useState(false);
  const [email, setEmail] = useState("");
  return (
    <>
      <div className={Styles.enterEmail}>
        <input
          className={Styles.input}
          onChange={(e) =>
            setEmail(e.target.checkValidity() ? e.target.value : "")
          }
          placeholder={strings.ENTER_EMAIL}
          type="email"
        />
        <button
          className={hasAgreed && email ? Styles.accent : Styles.light}
          disabled={!(hasAgreed && email)}
          onClick={(_) => register(email)}
          type="submit"
        >
          <p className={Styles.join} title={strings.JOIN}>
            {strings.JOIN} <ArrowRight />
          </p>
        </button>
      </div>
      <div className={Styles.acknowledgeContainer}>
        <Checkbox checked={hasAgreed} onChange={() => setHasAgreed(!hasAgreed)}>
          <div className={Styles.agreeLabel}>{strings.AGREE}</div>
        </Checkbox>
        <button
          className={Styles.termsOfUse}
          onClick={() => window.open(termsOfUseUrl, "_blank")}
          title={strings.TERMS}
          type="button"
        >
          {strings.TERMS}
        </button>
      </div>
      <div className={Styles.backToLoginContainer}>
        <button
          className={Styles.backToLogin}
          onClick={() => setEnterGuest(false)}
          title={strings.BACK_TO_LOGIN}
          type="button"
        >
          {strings.BACK_TO_LOGIN}
        </button>
      </div>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  waitingToEditID: state.context.workspaces.waitingToEditID,
});

const mapDispatchToProps = {
  login: login.request,
  registerEmail: registerEmail.request,
};

type PromptProps = ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(LoginPrompt);
